import { Approver } from '@/app/models/approver';
import { PaginationResponse } from '@/app/models/pagination';
import { formatDateToBrFormat } from '@/app/utils/dateUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TruncatedTextWithTooltip from '../common/truncatedTextWithTooltip';

interface ApproversTableProps {
  data: PaginationResponse<Approver>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  page: number;
  rowsPerPage: number;
  onDelete: CallableFunction;
  setSelectedApprover: (approver: Approver | null) => void;
}

export default function ApproversTable({
  data,
  setPage,
  setRowsPerPage,
  page,
  rowsPerPage,
  onDelete,
  setSelectedApprover,
}: ApproversTableProps) {
  const { t } = useTranslation(['approvers']);

  const columns: Column[] = [
    { key: 'system', label: t('system') },
    { key: 'quantity', label: t('collaborator') },
    { key: 'resultCenter', label: t('result.center') },
    { key: 'cluster', label: t('cluster') },
    { key: 'cell', label: t('cell') },
    { key: 'bussinessUnit', label: t('business.unit') },
    { key: 'expirationDate', label: t('expiration.date') },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (item: Approver) => {
    return (
      <>
        <TableCell sx={{ ...bodyCellSx }}>{item.system.name}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item.person.badge ?? ''} - ${item.person.name ?? ''}`}
            sx={bodyCellSx}
            size={200}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item.project.code ?? ''} - ${item.project.name ?? ''}`}
            sx={bodyCellSx}
            size={200}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item.project.cluster.code ?? ''} - ${item.project.cluster.name ?? ''}`}
            sx={bodyCellSx}
            size={20}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item.project.cell.code ?? ''} - ${item.project.cell.name ?? ''}`}
            sx={bodyCellSx}
            size={20}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item.project.businessUnit.code ?? ''} - ${item.project.businessUnit.name ?? ''}`}
            sx={bodyCellSx}
            size={20}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item?.expirationDate ? formatDateToBrFormat(item?.expirationDate) : ''}`}
            sx={bodyCellSx}
            size={20}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              setSelectedApprover(item);
              onDelete();
            }}
            aria-label="delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  // Use the approver ID as the unique key
  const getRowKey = (approver: Approver) => approver.id as number;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: data?.totalElements || 0,
    rowsPerPageOptions: [5, 10, 25],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('common:of')} ${count}`,
    labelRowsPerPage: t('common:items.per.page'),
  };

  return (
    <DataTable
      columns={columns}
      data={data?.content || []}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      pagination={paginationConfig}
      size="small"
    />
  );
}
